import React, { useState, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav';
import {
  getDrivers, getDriverRuns, getLocations, saveLocationDriverRun,
  getDriverAvailabilityTokens, saveDriverAvailabilityToken, updateDriverAvailabilityTokenReminder,
  updateDriver,
} from '../services/firebase';
import './Admin.css';

const DAYS = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'];

// Driver runs vary by day (Thu/Fri/Sat have 6 runs; Sunday has 5 — no 2:30 PM split-shift run)
function getRunsForDay(day) {
  const isSunday = day === 'Sun-April-12';
  if (isSunday) {
    return [
      { time: '6:30 AM',  dropoff: '7-10 AM',    pickup: null           },
      { time: '9:30 AM',  dropoff: '10 AM-1 PM',  pickup: '7-10 AM'     },
      { time: '12:30 PM', dropoff: '1-5 PM',      pickup: '10 AM-1 PM'  },
      { time: '4:30 PM',  dropoff: '5-8 PM',      pickup: '1-5 PM'      },
      { time: '7:30 PM',  dropoff: null,           pickup: '5-8 PM'      },
    ];
  }
  return [
    { time: '6:30 AM',  dropoff: '7-10 AM',    pickup: null           },
    { time: '9:30 AM',  dropoff: '10 AM-1 PM',  pickup: '7-10 AM'     },
    { time: '12:30 PM', dropoff: '1-3 PM',      pickup: '10 AM-1 PM'  },
    { time: '2:30 PM',  dropoff: '3-5 PM',      pickup: '1-3 PM'      },
    { time: '4:30 PM',  dropoff: '5-8 PM',      pickup: '3-5 PM'      },
    { time: '7:30 PM',  dropoff: null,           pickup: '5-8 PM'      },
  ];
}

const DEFAULT_LOCATIONS = [
  'Downtown Augusta',
  'James Brown Blvd',
  'Riverwalk',
  'Surrey Center',
  'Washington Road',
];

const DAY_KEY_MAP = {
  'Thu-April-9': 'thu',
  'Fri-April-10': 'fri',
  'Sat-April-11': 'sat',
  'Sun-April-12': 'sun',
};

function formatLastViewed(date) {
  if (!date) return '';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatDate(ts) {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : ts?.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const CONTACT_STATUSES = [
  { value: 'called',        label: 'Called' },
  { value: 'left-vm',       label: 'Left VM' },
  { value: 'no-answer',     label: 'No Answer' },
  { value: 'wrong-number',  label: 'Wrong Number' },
  { value: 'manual-email',  label: 'Sent Email (PWI Augusta)' },
];
function statusLabel(s) {
  return CONTACT_STATUSES.find(x => x.value === s)?.label || '';
}

function DriversAdmin({ onLogout }) {
  const [drivers, setDrivers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [runAssignments, setRunAssignments] = useState({}); // { `${day}_${runTime}_${location}`: [id, ...] }
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);
  const [activeTab, setActiveTab] = useState('schedule');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Availability request tab state
  const [driverTokens, setDriverTokens] = useState([]);
  const [availFilter, setAvailFilter] = useState('none'); // none | all | sent | responded
  const [availSelected, setAvailSelected] = useState(new Set());
  const [availSending, setAvailSending] = useState(false);
  const [availSendResult, setAvailSendResult] = useState(null);
  const [driverCopiedId, setDriverCopiedId] = useState(null);
  const [driverGeneratingId, setDriverGeneratingId] = useState(null);
  const [driverBannerDismissed, setDriverBannerDismissed] = useState(false);

  // Read last-viewed timestamp once on mount; first-ever visit sets baseline and returns null
  const [driverLastViewedDate] = useState(() => {
    const stored = localStorage.getItem('driverAvailabilityLastViewed');
    if (stored) return new Date(stored);
    localStorage.setItem('driverAvailabilityLastViewed', new Date().toISOString());
    return null;
  });

  const handleDriverMarkSeen = () => {
    localStorage.setItem('driverAvailabilityLastViewed', new Date().toISOString());
    setDriverBannerDismissed(true);
  };

  const [driverReminding, setDriverReminding] = useState(false);
  const [driverReminderResult, setDriverReminderResult] = useState(null);
  const [driverExpandedContactId, setDriverExpandedContactId] = useState(null);
  const [driverContactDraft, setDriverContactDraft] = useState({ status: '', notes: '' });
  const [driverContactSaving, setDriverContactSaving] = useState(false);
  const [availSortField, setAvailSortField] = useState('name');
  const [availSortDir, setAvailSortDir] = useState('asc');

  const handleSendDriverReminders = async () => {
    setDriverReminding(true);
    setDriverReminderResult(null);
    let successCount = 0, failCount = 0;

    // If drivers are selected, remind only those selected who have a pending token
    // Otherwise remind all pending
    const pendingDrivers = drivers.filter(d => {
      const tok = driverTokenMap[d.id];
      const isPending = tok && !tok.used;
      return availSelected.size > 0 ? availSelected.has(d.id) && isPending : isPending;
    });

    for (const drv of pendingDrivers) {
      try {
        const tok = driverTokenMap[drv.id];
        if (!drv.email) { failCount++; continue; }
        await fetch('/api/send-driver-availability', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ driverId: drv.id, firstName: drv.firstName, token: tok.token, to: drv.email, isReminder: true }),
        });
        await updateDriverAvailabilityTokenReminder(tok.token);
        successCount++;
      } catch {
        failCount++;
      }
    }

    setDriverReminderResult({ successCount, failCount });
    await loadData();
    setDriverReminding(false);
  };

  const toggleDriverContact = (drv) => {
    if (driverExpandedContactId === drv.id) {
      setDriverExpandedContactId(null);
    } else {
      setDriverExpandedContactId(drv.id);
      setDriverContactDraft({ status: drv.availContactStatus || '', notes: drv.availContactNotes || '' });
    }
  };

  const handleSaveDriverContact = async (drvId) => {
    setDriverContactSaving(true);
    await updateDriver(drvId, {
      availContactStatus: driverContactDraft.status,
      availContactNotes: driverContactDraft.notes,
      availContactUpdatedAt: driverContactDraft.status ? new Date().toISOString() : null,
    });
    await loadData();
    setDriverContactSaving(false);
    setDriverExpandedContactId(null);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    const [driverData, runData, locationData, tokenData] = await Promise.all([
      getDrivers(),
      getDriverRuns(),
      getLocations(),
      getDriverAvailabilityTokens(),
    ]);
    setDrivers(driverData);
    setDriverTokens(tokenData);

    const activeLocations = (locationData.length > 0
      ? locationData.filter(l => l.active).map(l => l.name)
      : DEFAULT_LOCATIONS
    ).sort((a, b) => a.localeCompare(b));
    setLocations(activeLocations);

    const runMap = {};
    runData.forEach(r => {
      // Only load new-format docs (have runTime and location fields); skip old-format
      if (r.runTime && r.location) {
        runMap[`${r.day}_${r.runTime}_${r.location}`] = r.drivers || [];
      }
    });
    setRunAssignments(runMap);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Build a map: driverId → most recent token doc
  const driverTokenMap = {};
  driverTokens.forEach(t => {
    const existing = driverTokenMap[t.driverId];
    if (!existing || (t.sentAt?.seconds || 0) > (existing.sentAt?.seconds || 0)) {
      driverTokenMap[t.driverId] = t;
    }
  });

  const getDriverAvailStatus = (driver) => {
    const tok = driverTokenMap[driver.id];
    const hasAvail = ['thuAvailability', 'friAvailability', 'satAvailability', 'sunAvailability']
      .some(f => driver[f] && driver[f] !== '');
    if (hasAvail || tok?.used) return 'responded';
    if (tok && !tok.used) return 'sent';
    return 'none';
  };

  const handleGetDriverLink = async (drv) => {
    const tok = driverTokenMap[drv.id];
    let link;
    if (tok && !tok.used) {
      link = `https://www.augustapwi.com/driver-availability?t=${tok.token}`;
    } else {
      setDriverGeneratingId(drv.id);
      const token = crypto.randomUUID();
      await saveDriverAvailabilityToken(drv.id, token, 'manual');
      await loadData();
      link = `https://www.augustapwi.com/driver-availability?t=${token}`;
      setDriverGeneratingId(null);
    }
    await navigator.clipboard.writeText(link);
    setDriverCopiedId(drv.id);
    setTimeout(() => setDriverCopiedId(null), 2500);
  };

  const handleSendAvailRequests = async () => {
    setAvailSending(true);
    setAvailSendResult(null);
    let successCount = 0, failCount = 0;

    const toSend = drivers.filter(d => availSelected.has(d.id));

    for (const drv of toSend) {
      try {
        if (!drv.email) { failCount++; continue; }
        const token = crypto.randomUUID();
        await saveDriverAvailabilityToken(drv.id, token, 'email');
        await fetch('/api/send-driver-availability', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ driverId: drv.id, firstName: drv.firstName, token, to: drv.email }),
        });
        successCount++;
      } catch {
        failCount++;
      }
    }

    setAvailSendResult({ successCount, failCount });
    setAvailSelected(new Set());
    await loadData();
    setAvailSending(false);
  };

  const getAvailableDrivers = (day) => {
    const dayKey = DAY_KEY_MAP[day];
    return drivers
      .filter(d => {
        const avail = d[`${dayKey}Availability`];
        return avail && avail !== 'Not Available';
      })
      .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
  };

  const saveRun = async (day, runTime, location, driverList) => {
    setSaving(true);
    await saveLocationDriverRun(day, runTime, location, driverList);
    setSaving(false);
  };

  const handleDriverChange = async (day, runTime, location, idx, driverId) => {
    const key = `${day}_${runTime}_${location}`;
    const current = [...(runAssignments[key] || [''])];
    current[idx] = driverId;
    setRunAssignments(prev => ({ ...prev, [key]: current }));
    await saveRun(day, runTime, location, current);
  };

  const handleAddDriver = async (day, runTime, location) => {
    const key = `${day}_${runTime}_${location}`;
    const current = [...(runAssignments[key] || ['']), ''];
    setRunAssignments(prev => ({ ...prev, [key]: current }));
    await saveRun(day, runTime, location, current);
  };

  const handleRemoveDriver = async (day, runTime, location, idx) => {
    const key = `${day}_${runTime}_${location}`;
    const current = (runAssignments[key] || []).filter((_, i) => i !== idx);
    setRunAssignments(prev => ({ ...prev, [key]: current }));
    await saveRun(day, runTime, location, current);
  };

  const getDriverInfo = (driverId) => drivers.find(d => d.id === driverId);

  const filteredDrivers = drivers
    .filter(d => `${d.firstName} ${d.lastName} ${d.congregation}`.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));

  if (loading) {
    return <div className="admin-container"><div className="loading">Loading...</div></div>;
  }

  const availableToday = getAvailableDrivers(selectedDay);

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Driver Scheduling</h1>
        <p className="page-subtitle">Assign drivers to shuttle runs for drop-off and pick-up on each day.</p>
      </header>

      <div className="scheduling-content">
        <div className="settings-tabs">
          <button
            className={activeTab === 'schedule' ? 'active' : ''}
            onClick={() => setActiveTab('schedule')}
          >
            Run Schedule
          </button>
          <button
            className={activeTab === 'list' ? 'active' : ''}
            onClick={() => setActiveTab('list')}
          >
            All Drivers ({drivers.length})
          </button>
          <button
            className={activeTab === 'avail' ? 'active' : ''}
            onClick={() => setActiveTab('avail')}
          >
            Availability Requests
          </button>
        </div>

        {/* Run Schedule Tab */}
        {activeTab === 'schedule' && (
          <>
            <div className="day-selector" style={{ marginTop: '20px' }}>
              {DAYS.map(day => (
                <button
                  key={day}
                  className={`day-tab ${selectedDay === day ? 'active' : ''}`}
                  onClick={() => setSelectedDay(day)}
                >
                  {day}
                  <span className="day-avail-count"> ({getAvailableDrivers(day).length} avail)</span>
                </button>
              ))}
            </div>

            {saving && <div className="save-indicator">💾 Saving...</div>}

            <p className="section-note" style={{ marginBottom: '15px' }}>
              {availableToday.length} driver{availableToday.length !== 1 ? 's' : ''} available on {selectedDay}.
            </p>

            <div className="schedule-grid">
              {getRunsForDay(selectedDay).map(run => {
                const label = [
                  run.dropoff ? `Drop-off: ${run.dropoff}` : null,
                  run.pickup  ? `Pick-up: ${run.pickup}`   : null,
                ].filter(Boolean).join(' | ');

                return (
                  <div key={run.time} className="location-section">
                    <div className="shift-header" style={{ padding: '15px 20px', background: '#f5f5f5', borderRadius: '8px 8px 0 0' }}>
                      <div className="shift-time">🚐 {run.time} — {label}</div>
                    </div>

                    <div style={{ padding: '10px 20px 15px' }}>
                      {locations.map(location => {
                        const key = `${selectedDay}_${run.time}_${location}`;
                        const current = runAssignments[key] || [];
                        const slots = current.length > 0 ? current : [''];
                        const filledCount = current.filter(Boolean).length;

                        return (
                          <div key={location} className="drivers-location-row">
                            <div className="drivers-location-label">
                              📍 {location}
                              {filledCount > 0 && (
                                <span className="drivers-filled-count">
                                  {' '}({filledCount} driver{filledCount !== 1 ? 's' : ''})
                                </span>
                              )}
                            </div>

                            <div className="dayof-driver-slots">
                              {slots.map((driverId, i) => {
                                const di = driverId ? getDriverInfo(driverId) : null;
                                return (
                                  <div key={i} className="dayof-driver-slot">
                                    <label>Driver {i + 1}:</label>
                                    <select
                                      value={driverId || ''}
                                      onChange={e => handleDriverChange(selectedDay, run.time, location, i, e.target.value)}
                                    >
                                      <option value="">— Select Driver —</option>
                                      {availableToday.map(d => (
                                        <option key={d.id} value={d.id}>
                                          {d.lastName}, {d.firstName} — {d.vehicleType} ({d.passengerCapacity} pass)
                                        </option>
                                      ))}
                                    </select>
                                    {di && di.phone && (
                                      <a href={`tel:${di.phone}`} className="dayof-phone drivers-phone-link">{di.phone}</a>
                                    )}
                                    {i > 0 && (
                                      <button
                                        className="dayof-driver-remove-btn"
                                        onClick={() => handleRemoveDriver(selectedDay, run.time, location, i)}
                                        title="Remove driver"
                                      >×</button>
                                    )}
                                  </div>
                                );
                              })}
                              <button
                                className="dayof-add-driver-btn"
                                onClick={() => handleAddDriver(selectedDay, run.time, location)}
                              >+ Add Driver</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* All Drivers List Tab */}
        {activeTab === 'list' && (
          <div style={{ marginTop: '20px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search drivers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                style={{ maxWidth: '350px' }}
              />
            </div>

            {/* Legend */}
            <div className="driver-avail-legend">
              <span className="legend-label">Availability:</span>
              <span className="legend-item"><span className="avail-badge anytime">Day</span> All day</span>
              <span className="legend-item"><span className="avail-badge partial">Day <small>2</small></span> Some slots</span>
              <span className="legend-item"><span className="avail-badge unavail">✗ Day</span> Not available</span>
              <span className="legend-item"><span className="avail-badge no-response">Day</span> Not yet provided</span>
            </div>

            {filteredDrivers.length === 0 ? (
              <div className="no-data">No drivers found.</div>
            ) : (
              <div className="driver-cards">
                {filteredDrivers.map(driver => (
                  <div key={driver.id} className="driver-card">
                    <div className="driver-card-header">
                      <div className="driver-name">{driver.lastName}, {driver.firstName}</div>
                      <div className="driver-cong">{driver.congregation || 'No congregation'}</div>
                    </div>

                    <div className="driver-card-body">
                      <div className="driver-detail">
                        <strong>Vehicle:</strong> {driver.vehicleType || 'N/A'} &bull; {driver.passengerCapacity || '?'} passengers &bull; {driver.cartCapacity || '?'} carts
                      </div>
                      {driver.phone && (
                        <div className="driver-detail">
                          <a href={`tel:${driver.phone}`} className="btn-call-small" style={{ display: 'inline-block' }}>
                            Call {driver.phone}
                          </a>
                        </div>
                      )}
                      {driver.email && (
                        <div className="driver-detail" style={{ color: '#666', fontSize: '13px' }}>{driver.email}</div>
                      )}
                      {driver.alsoDoingCartShifts === 'Yes' && (
                        <div className="driver-detail" style={{ color: '#667eea', fontSize: '13px' }}>Also doing cart shifts</div>
                      )}
                    </div>

                    <div className="driver-availability">
                      {DAYS.map(day => {
                        const dayKey = DAY_KEY_MAP[day];
                        const avail = driver[`${dayKey}Availability`];
                        const label = day.split('-')[0];

                        // Blank / not yet provided
                        if (!avail || avail === '') {
                          return <span key={day} className="avail-badge no-response" title="Not yet provided">{label}</span>;
                        }
                        // Explicitly unavailable
                        if (avail === 'Not Available') {
                          return <span key={day} className="avail-badge unavail" title="Not available">✗ {label}</span>;
                        }
                        // Available all day
                        if (avail === 'Anytime') {
                          return <span key={day} className="avail-badge anytime" title="Available all day">{label}</span>;
                        }
                        // Specific slots
                        const count = Array.isArray(avail) ? avail.length : 1;
                        const tooltip = Array.isArray(avail) ? avail.join(', ') : avail;
                        return (
                          <span key={day} className="avail-badge partial" title={tooltip}>
                            {label} <small>{count}</small>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Availability Requests Tab */}
        {activeTab === 'avail' && (() => {
          const availStats = {
            total: drivers.length,
            none: drivers.filter(d => getDriverAvailStatus(d) === 'none').length,
            sent: drivers.filter(d => getDriverAvailStatus(d) === 'sent').length,
            responded: drivers.filter(d => getDriverAvailStatus(d) === 'responded').length,
            contacted: drivers.filter(d => !!d.availContactStatus).length,
          };

          const handleAvailSort = (field) => {
            if (field === availSortField) setAvailSortDir(d => d === 'asc' ? 'desc' : 'asc');
            else { setAvailSortField(field); setAvailSortDir('asc'); }
          };
          const availSortMult = availSortDir === 'asc' ? 1 : -1;
          const byDrvName = (a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '');

          const availFiltered = [...drivers]
            .filter(d => {
              if (availFilter === 'contacted') return !!d.availContactStatus;
              return availFilter === 'all' ? true : getDriverAvailStatus(d) === availFilter;
            })
            .sort((a, b) => {
              if (availSortField === 'name')    return availSortMult * byDrvName(a, b);
              if (availSortField === 'status')  return availSortMult * (getDriverAvailStatus(a).localeCompare(getDriverAvailStatus(b))) || byDrvName(a, b);
              if (availSortField === 'contact') return availSortMult * ((a.availContactStatus || '').localeCompare(b.availContactStatus || '')) || byDrvName(a, b);
              return byDrvName(a, b);
            });

          const allSelected = availFiltered.length > 0 && availFiltered.every(d => availSelected.has(d.id));

          // New responses since last visit
          const newDriverResponses = (!driverBannerDismissed && driverLastViewedDate)
            ? driverTokens
                .filter(t => {
                  if (!t.used || !t.respondedAt) return false;
                  const d = t.respondedAt?.toDate ? t.respondedAt.toDate()
                          : t.respondedAt?.seconds ? new Date(t.respondedAt.seconds * 1000)
                          : new Date(t.respondedAt);
                  return d > driverLastViewedDate;
                })
                .map(t => drivers.find(d => d.id === t.driverId))
                .filter(Boolean)
                .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
            : [];

          return (
            <div style={{ marginTop: '20px' }}>
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
                Send an email to drivers asking them to confirm their vehicle details and availability via a secure link.
              </p>

              {/* New responses banner */}
              {newDriverResponses.length > 0 && (
                <div className="avail-new-banner">
                  <div>
                    <strong>✨ {newDriverResponses.length} new response{newDriverResponses.length !== 1 ? 's' : ''}</strong>
                    {' '}since your last visit ({formatLastViewed(driverLastViewedDate)})
                  </div>
                  <div className="avail-new-names">
                    {newDriverResponses.slice(0, 6).map(d => `${d.lastName}, ${d.firstName}`).join(' · ')}
                    {newDriverResponses.length > 6 && ` · +${newDriverResponses.length - 6} more`}
                  </div>
                  <button className="avail-new-dismiss" onClick={handleDriverMarkSeen}>Mark as seen ✓</button>
                </div>
              )}

              {/* Stat cards */}
              <div className="stats-grid" style={{ marginBottom: '16px' }}>
                <div className="stat-card"><div className="stat-number">{availStats.total}</div><div className="stat-label">Total Drivers</div></div>
                <div className="stat-card" style={{ borderLeft: '4px solid #dc2626' }}><div className="stat-number" style={{ color: '#dc2626' }}>{availStats.none}</div><div className="stat-label">Not Yet Sent</div></div>
                <div className="stat-card" style={{ borderLeft: '4px solid #ca8a04' }}><div className="stat-number" style={{ color: '#ca8a04' }}>{availStats.sent}</div><div className="stat-label">Sent (Pending)</div></div>
                <div className="stat-card info"><div className="stat-number">{availStats.responded}</div><div className="stat-label">Responded</div></div>
              </div>

              {/* Filter pills */}
              <div className="filter-pill-row" style={{ marginBottom: '16px' }}>
                {[
                  { key: 'none',      label: `Not Yet Sent (${availStats.none})` },
                  { key: 'all',       label: `All Drivers (${availStats.total})` },
                  { key: 'sent',      label: `Sent (${availStats.sent})` },
                  { key: 'responded', label: `Responded (${availStats.responded})` },
                  { key: 'contacted', label: `Contacted (${availStats.contacted})` },
                ].map(f => (
                  <button
                    key={f.key}
                    className={`filter-pill ${availFilter === f.key ? 'active' : ''}`}
                    onClick={() => { setAvailFilter(f.key); setAvailSelected(new Set()); }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Action buttons — always visible */}
              {(() => {
                const selectedPendingCount = [...availSelected].filter(id => {
                  const tok = driverTokenMap[id];
                  return tok && !tok.used;
                }).length;
                const reminderCount = availSelected.size > 0 ? selectedPendingCount : availStats.sent;
                return (
                  <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      className="btn btn-primary"
                      disabled={availSelected.size === 0 || availSending}
                      onClick={handleSendAvailRequests}
                    >
                      {availSending ? 'Sending…' : `Send Requests (${availSelected.size} selected)`}
                    </button>
                    <button
                      className="btn btn-secondary"
                      disabled={reminderCount === 0 || driverReminding || availSending}
                      onClick={handleSendDriverReminders}
                      title={availSelected.size > 0
                        ? 'Send reminder to selected drivers who have a pending token'
                        : 'Resend the same link to all drivers sent a request but who have not yet responded'}
                    >
                      {driverReminding ? 'Sending reminders…'
                        : availSelected.size > 0
                          ? `Send Reminder to selected (${selectedPendingCount} pending)`
                          : `Send Reminder to all pending (${availStats.sent})`}
                    </button>
                  </div>
                );
              })()}
              {availSendResult && (
                <div className={`result-message ${availSendResult.failCount === 0 ? 'success' : 'error'}`} style={{ marginBottom: '16px' }}>
                  Sent {availSendResult.successCount} request{availSendResult.successCount !== 1 ? 's' : ''}
                  {availSendResult.failCount > 0 && ` · ${availSendResult.failCount} skipped (no email on file)`}.
                </div>
              )}
              {driverReminderResult && (
                <div className={`result-message ${driverReminderResult.failCount === 0 ? 'success' : 'error'}`} style={{ marginBottom: '16px' }}>
                  Reminder sent to {driverReminderResult.successCount} driver{driverReminderResult.successCount !== 1 ? 's' : ''}
                  {driverReminderResult.failCount > 0 && ` · ${driverReminderResult.failCount} skipped (no email on file)`}.
                </div>
              )}

              {availFiltered.length === 0 ? (
                <div className="no-data">No drivers in this category.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="avail-vol-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input type="checkbox" checked={allSelected} onChange={e => {
                            if (e.target.checked) setAvailSelected(new Set(availFiltered.map(d => d.id)));
                            else setAvailSelected(new Set());
                          }} title="Select all" />
                        </th>
                        <th className="vol-load-sort-th" onClick={() => handleAvailSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Name{' '}{availSortField === 'name' ? (availSortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.3 }}>⇅</span>}
                        </th>
                        <th>Email</th>
                        <th className="vol-load-sort-th" onClick={() => handleAvailSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Status{' '}{availSortField === 'status' ? (availSortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.3 }}>⇅</span>}
                        </th>
                        <th>Last Contact</th>
                        <th className="vol-load-sort-th" onClick={() => handleAvailSort('contact')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Outreach{' '}{availSortField === 'contact' ? (availSortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.3 }}>⇅</span>}
                        </th>
                        <th>Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availFiltered.map(drv => {
                        const s = getDriverAvailStatus(drv);
                        return (
                          <React.Fragment key={drv.id}>
                            <tr>
                              <td>
                                <input type="checkbox" checked={availSelected.has(drv.id)} onChange={() => {
                                  setAvailSelected(prev => {
                                    const next = new Set(prev);
                                    if (next.has(drv.id)) next.delete(drv.id); else next.add(drv.id);
                                    return next;
                                  });
                                }} />
                              </td>
                              <td>
                                {drv.lastName}, {drv.firstName}
                                {drv.isDeafHH && <span className="roster-badge badge-deaf" title="Deaf / Hard of Hearing — email/text only" style={{ marginLeft: 5, fontSize: '11px' }}>🦻 D/HH</span>}
                              </td>
                              <td style={{ color: drv.email ? '#1e293b' : '#94a3b8', fontSize: '13px' }}>
                                {drv.email || 'No email on file'}
                              </td>
                              <td>
                                <span className={`avail-status-badge ${s === 'responded' ? 'done' : s === 'sent' ? 'sent' : 'none'}`}>
                                  {s === 'responded' ? 'Responded' : s === 'sent' ? 'Sent' : 'Not Sent'}
                                </span>
                              </td>
                              <td className="avail-last-contact-cell">
                                {driverTokenMap[drv.id]?.sentAt && (
                                  <div>Sent: {formatDate(driverTokenMap[drv.id].sentAt)}</div>
                                )}
                                {driverTokenMap[drv.id]?.reminderSentAt && (
                                  <div className="avail-reminder-date">Reminder: {formatDate(driverTokenMap[drv.id].reminderSentAt)}</div>
                                )}
                              </td>
                              <td>
                                {drv.availContactStatus ? (
                                  <div>
                                    <button
                                      className={`contact-status-badge ${drv.availContactStatus}`}
                                      onClick={() => toggleDriverContact(drv)}
                                      title="Edit contact log"
                                    >
                                      {statusLabel(drv.availContactStatus)}
                                    </button>
                                    {drv.availContactUpdatedAt && (
                                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                                        {new Date(drv.availContactUpdatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    className="btn-log-contact"
                                    title="Log outreach attempt"
                                    onClick={() => toggleDriverContact(drv)}
                                  >
                                    + Log
                                  </button>
                                )}
                              </td>
                              <td>
                                {s !== 'responded' && (
                                  <button
                                    className="btn-get-link"
                                    onClick={() => handleGetDriverLink(drv)}
                                    disabled={driverGeneratingId === drv.id}
                                    title="Copy link to clipboard for manual SMS"
                                  >
                                    {driverGeneratingId === drv.id ? 'Generating…'
                                     : driverCopiedId === drv.id   ? '✓ Copied!'
                                     : driverTokenMap[drv.id] && !driverTokenMap[drv.id].used ? '📋 Copy Link'
                                     : '🔗 Get Link'}
                                  </button>
                                )}
                              </td>
                            </tr>
                            {driverExpandedContactId === drv.id && (
                              <tr>
                                <td colSpan={7} style={{ padding: 0, background: '#f8fafc' }}>
                                  <div className="contact-log-panel">
                                    <div className="contact-log-status-row">
                                      {CONTACT_STATUSES.map(cs => (
                                        <label key={cs.value} className="contact-log-radio">
                                          <input
                                            type="radio"
                                            name={`driver-contact-${drv.id}`}
                                            value={cs.value}
                                            checked={driverContactDraft.status === cs.value}
                                            onChange={() => setDriverContactDraft(d => ({ ...d, status: cs.value }))}
                                          />
                                          {cs.label}
                                        </label>
                                      ))}
                                      {driverContactDraft.status && (
                                        <button className="contact-log-clear" onClick={() => setDriverContactDraft(d => ({ ...d, status: '' }))}>✕ Clear</button>
                                      )}
                                    </div>
                                    <textarea
                                      className="contact-log-notes"
                                      placeholder="Notes (optional — e.g. callback time, reason)…"
                                      value={driverContactDraft.notes}
                                      onChange={e => setDriverContactDraft(d => ({ ...d, notes: e.target.value }))}
                                      rows={2}
                                    />
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                      <button className="btn btn-primary" style={{ fontSize: '13px', padding: '4px 14px' }} onClick={() => handleSaveDriverContact(drv.id)} disabled={driverContactSaving}>
                                        {driverContactSaving ? 'Saving…' : 'Save'}
                                      </button>
                                      <button className="btn btn-secondary" style={{ fontSize: '13px', padding: '4px 14px' }} onClick={() => setDriverExpandedContactId(null)}>
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default DriversAdmin;
